import React from 'react'
import { HelpCircle } from 'lucide-react'
import { Input } from '../../components/input'
import { UILabel } from './ui-label'
import { UITypography } from '../display/ui-typography'
import { UITooltip } from '../overlay/ui-tooltip'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

// Base Input export - for other composed components
export { Input as UIInputBase } from '../../components/input'

export interface UIInputProps extends React.ComponentProps<typeof Input> {
  label?: string
  labelId?: string
  /** Optional help tooltip rendered as info icon next to the label. */
  labelHelp?: React.ReactNode
  description?: string
  error?: string
  helperText?: string
  required?: boolean
  containerClassName?: string
}

/**
 * 통합 입력 필드 컴포넌트
 * 라벨, 도움말 텍스트, 오류 메시지를 통합하여 일관된 사용자 인터페이스를 제공합니다.
 */
export const UIInput = React.forwardRef<
  React.ComponentRef<typeof Input>,
  UIInputProps
>(({
  className,
  label,
  labelId,
  labelHelp,
  description,
  error,
  helperText,
  required = false,
  containerClassName,
  ...props
}, ref) => {
  const inputId = labelId || props.id
  const hasMeta = !!(label || description || error || helperText)

  return (
    <UIBox className={cn(hasMeta ? "space-y-2" : "contents", containerClassName)}>
      {label && (
        labelHelp ? (
          <UIBox className="flex items-center gap-1">
            <UILabel
              htmlFor={inputId}
              className={cn(
                "text-sm font-medium leading-none",
                required && "after:content-['*'] after:ml-0.5 after:text-destructive",
                error && "text-destructive"
              )}
            >
              {label}
            </UILabel>
            <UITooltip content={labelHelp} side="top">
              <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
            </UITooltip>
          </UIBox>
        ) : (
          <UILabel
            htmlFor={inputId}
            className={cn(
              "text-sm font-medium leading-none",
              required && "after:content-['*'] after:ml-0.5 after:text-destructive",
              error && "text-destructive"
            )}
          >
            {label}
          </UILabel>
        )
      )}

      {description && (
        <UITypography className="text-xs text-muted-foreground">{description}</UITypography>
      )}

      <Input
        ref={ref}
        id={inputId}
        required={required}
        className={cn(
          error && "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20",
          className
        )}
        aria-invalid={error ? 'true' : undefined}
        aria-required={required ? 'true' : undefined}
        aria-describedby={
          error ? `${inputId}-error` :
          helperText ? `${inputId}-helper` :
          undefined
        }
        {...props}
      />

      {error && (
        <UITypography
          as="p"
          id={`${inputId}-error`}
          className="text-xs text-destructive"
          role="alert"
        >
          {error}
        </UITypography>
      )}

      {helperText && !error && (
        <UITypography
          as="p"
          id={`${inputId}-helper`}
          className="text-xs text-muted-foreground"
        >
          {helperText}
        </UITypography>
      )}
    </UIBox>
  )
})

UIInput.displayName = "UIInput"
